import { NumberValue } from 'elements/minute/sidebar/sidebarComponents/atomic/NumberValue';
import { useAccess } from 'hooks/access/useAccess';
import { EFonts } from 'libs/styles/styling';
import Slider from 'rc-slider';
import { useState } from 'react';
import styled from 'styled-components/macro';
import { useUpdateEffect } from 'usehooks-ts';

interface Props {
    className?: string;
    label?: string;
    steps: number[];
    value: number;
    forceManualValue?: 'SHOWN' | 'HIDDEN';
    onValueChange?: (value: number) => void;
}

export const SliderValue = (props: Props): JSX.Element => {
    const [value, setValue] = useState(props.value);
    const { isExperimental } = useAccess();

    const selectedIndex = props.steps.findIndex((e) => e == value);

    useUpdateEffect(() => {
        if (props.value !== value) {
            props.onValueChange?.(value);
        }
    }, [value]);

    useUpdateEffect(() => {
        if (props.value !== value) {
            setValue(props.value);
        }
    }, [props.value]);

    // Structure to correctly display steps in the slider. Label ('-') is required but it's irrelevant
    const marks = Object.fromEntries(new Array(props.steps.length).fill(0).map((_, i) => [i, '-']));

    function showManualValue() {
        if (props.forceManualValue === 'SHOWN') {
            return true;
        } else if (props.forceManualValue === 'HIDDEN') {
            return false;
        }
        return isExperimental;
    }

    return (
        <S_SliderValue className={props.className}>
            <S_SliderContainer>
                <Slider
                    min={0}
                    max={props.steps.length - 1}
                    value={selectedIndex === -1 ? null : selectedIndex}
                    marks={marks}
                    onChange={(value) => {
                        setValue(props.steps[value as number]);
                    }}
                />
                {showManualValue() && (
                    <S_Mid>
                        <S_NumberValue
                            value={value}
                            onChange={(e) => {
                                setValue(e);
                            }}
                        />
                    </S_Mid>
                )}
            </S_SliderContainer>
            {props.label && <S_Label>{props.label}</S_Label>}
        </S_SliderValue>
    );
};

const S_SliderValue = styled.div`
    width: 100%;
`;

const S_Label = styled.div`
    color: #141c54;
    font-size: 13px;
    font-family: ${EFonts.Regular};
    width: 100%;
    text-align: center;
    user-select: none;
    margin: 15px 0 0 0;
`;

const S_SliderContainer = styled.div`
    .rc-slider {
        position: relative;
        width: 100%;
        height: 9px;
        border-radius: 7px;
        touch-action: none;
        box-sizing: border-box;
        border: 1px solid #dadada;
        background: #f5f5f5;
        box-shadow: 0px 0px 10px 0px #dedede inset;
    }

    .rc-slider-rail {
        position: absolute;
        width: 100%;
        height: 4px;
        background-color: #e9e9e9;
        border-radius: 6px;
    }

    .rc-slider-track {
        position: absolute;
        height: 100%;
        background-color: #f4f4f4;
        border-radius: 7px;
    }

    .rc-slider-step {
        position: absolute;
        width: 100%;
        height: 4px;
        background: transparent;
    }

    .rc-slider-handle {
        position: absolute;
        z-index: 1;
        width: 25px;
        height: 25px;
        top: -8px;
        background-color: #f4f4f4;
        border: solid 1px #dadada;
        border-radius: 50%;
        cursor: pointer;
        cursor: grab;
        touch-action: pan-x;
    }

    .rc-slider-mark {
        display: none;
    }

    .rc-slider-dot {
        position: absolute;
        bottom: -7.5px;
        width: 3px;
        height: 15px;
        background-color: #dadada;
        cursor: pointer;
        &:first-of-type,
        &:last-of-type {
            opacity: 0.01;
        }
        &:before {
            position: absolute;
            content: '';
            top: -20px;
            right: -20px;
            left: -20px;
            bottom: -20px;
            z-index: 40;
            cursor: pointer;
        }
    }

    .rc-slider-dot-active {
        border-color: #96dbfa;
    }
`;

const S_Mid = styled.div`
    display: flex;
    justify-content: center;
`;

const S_NumberValue = styled(NumberValue)`
    margin: 20px 0 0 0;
`;
